// Shared, app-level access to the host app's native bridge.
//
// `window.NativeBridge.performAction(actionId, params)` is injected by the host
// (iOS/Android) WebView. This module is intentionally generic — the
// adhoc-surge-banner module keeps its own copy for that feature's use case; this
// one is for app-wide needs (boot gate, language, theme, …).

declare global {
  interface Window {
    NativeBridge?: {
      ready: () => Promise<unknown>;
      performAction: <T = unknown>(actionId: string, params: object) => Promise<T>;
    };
  }
}

const LOG_PREFIX = '[NativeBridge]';
const SHIM_POLL_INTERVAL_MS = 100;
const SHIM_POLL_MAX_ATTEMPTS = 20; // ~2s total

let readyPromise: Promise<void> | null = null;

// Resolves once `window.NativeBridge` is injected AND its `ready()` handshake
// completes. Rejects if the shim is not injected within ~2s
// (SHIM_POLL_INTERVAL_MS * SHIM_POLL_MAX_ATTEMPTS).
//
// NOTE: only the shim-injection wait is bounded by the ~2s cap — `ready()` itself
// is not. Callers that gate UI on this (e.g. App boot) should add their own hard
// timeout so they can never hang if `ready()` stalls.
//
// Memoized: once resolved it is reused by every caller; on rejection the cache is
// cleared so a later call can retry.
export const ensureBridgeReady = (): Promise<void> => {
  if (readyPromise) return readyPromise;

  const attempt = new Promise<void>((resolve, reject) => {
    let attempts = 0;
    const check = (): void => {
      if (window.NativeBridge && typeof window.NativeBridge.performAction === 'function') {
        window.NativeBridge.ready()
          .then(() => resolve())
          .catch((err) => reject(err));
        return;
      }
      if (++attempts >= SHIM_POLL_MAX_ATTEMPTS) {
        reject(new Error('NativeBridge shim not injected after ~2s'));
        return;
      }
      setTimeout(check, SHIM_POLL_INTERVAL_MS);
    };
    check();
  });

  readyPromise = attempt.catch((err) => {
    readyPromise = null; // allow a later retry
    throw err;
  });
  return readyPromise;
};

// Calls a native action after the bridge is ready. NEVER throws — returns `null`
// on any failure (bridge absent, ~2s timeout, or the native side rejecting), so a
// plain browser/WebView without the shim simply gets `null`.
export const performNativeAction = async <T = unknown>(
  actionId: string,
  params: object,
): Promise<T | null> => {
  console.log(`${LOG_PREFIX} → ${actionId}`, params);
  try {
    await ensureBridgeReady();
    const result = await window.NativeBridge!.performAction<T>(actionId, params);
    console.log(`${LOG_PREFIX} ← ${actionId}`, result);
    return result;
  } catch (err) {
    console.warn(`${LOG_PREFIX} ✗ ${actionId}`, err);
    return null;
  }
};
